/**
 * Print branding — the admin-uploadable bits that appear on the invoice/
 * order print but have no column in the backend yet: the Authorised
 * Signatory image, an e-invoice/UPI QR image, and the company bank
 * details. Stored in localStorage (per browser) like `formats.ts`; logged
 * as a backend gap (G84/§70) so the Tenant can store these server-side
 * later. Images are uploaded via `persistImage` → /api/Image and only
 * their hosted URLs are kept here.
 */

import { apiClient } from '@kss/api';
import QRCode from 'qrcode';
import type { TenantHeader } from './templates';

export interface PrintBranding {
  /** Company logo shown at the top-left of every print header. */
  logoUrl?: string;
  signatureUrl?: string;
  qrUrl?: string;
  bankName?: string;
  bankAccountName?: string;
  bankAccountNo?: string;
  bankIfsc?: string;
  bankBranch?: string;
  // Tenant / seller header for prints. The backend exposes NO tenant-info
  // endpoint (`/tenants/Tenant/me` → 404, gap G86), so the admin sets these
  // once under Settings → Print Formats and every print reads them here.
  tenantName?: string;
  tenantAddress?: string;
  tenantCity?: string;
  tenantState?: string;
  tenantPincode?: string;
  tenantGstin?: string;
  tenantPan?: string;
  tenantPhone?: string;
  tenantEmail?: string;
}

const LS_KEY = 'kss-print-branding';

/**
 * Backend tenant/company profile, cached after the first fetch. Contrary to the
 * stale G86 note, `GET /tenants/Tenant/me` returns 200 with the real company
 * name + logoUrl (+ address/gstin/…). We cache it so the print header shows the
 * tenant's actual name (e.g. "BILL") and logo automatically — no manual Settings
 * config required. A manual Settings → Print Formats override still wins.
 */
let TENANT_CACHE: Partial<TenantHeader> | null = null;
/**
 * Backend signature/QR/bank columns, cached alongside the tenant header.
 * `/tenants/Tenant/me` returns signatureImageUrl, qrImageUrl and the five
 * bank* columns — they just aren't part of TenantHeader. We keep them here so
 * prints can fall back to the server-stored branding when the admin hasn't
 * uploaded a local override in Settings → Print Formats.
 */
let BRANDING_CACHE: Partial<PrintBranding> | null = null;
let priming: Promise<void> | null = null;

export function getCachedTenant(): Partial<TenantHeader> {
  return TENANT_CACHE ?? {};
}

/**
 * Company name from the persisted login envelope (redux-persist auth slice in
 * sessionStorage), used as a fallback for the print header when the backend
 * tenant/company endpoints are unavailable. The login response carries
 * `company.name` (e.g. "BILL"); reading it here means the header shows the real
 * company instead of the generic 'KSS BillBook' literal even offline / on 404.
 */
function loginCompanyName(): string | undefined {
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (!k || !/persist:kss.*auth/i.test(k)) continue;
      const blob = JSON.parse(sessionStorage.getItem(k) || '{}') as Record<string, string>;
      const user = blob.user ? (JSON.parse(blob.user) as { company?: { name?: string } | null }) : null;
      const name = user?.company?.name;
      if (name) return name;
    }
  } catch { /* sessionStorage blocked / malformed — fall through */ }
  return undefined;
}

/**
 * Fetch + cache the backend tenant/company profile ONCE. Idempotent and
 * fire-and-forget: callers (e.g. the panel root) can `void primeTenantBranding()`
 * on mount. Falls back silently — prints still work from localStorage/defaults.
 */
export async function primeTenantBranding(): Promise<void> {
  if (TENANT_CACHE || BRANDING_CACHE || priming) return priming ?? Promise.resolve();
  priming = (async () => {
    const readRow = (data: unknown): Record<string, unknown> => {
      const b = (data ?? {}) as Record<string, unknown>;
      if (Array.isArray(b)) return (b[0] ?? {}) as Record<string, unknown>;
      if (Array.isArray(b.data)) return (b.data[0] ?? {}) as Record<string, unknown>;
      return ((b.data as Record<string, unknown>) ?? b) ?? {};
    };
    let t: Record<string, unknown> = {};
    try { t = readRow((await apiClient.get('/tenants/Tenant/me')).data); } catch { /* try next */ }
    if (!t.name) { try { t = readRow((await apiClient.get('/organization/Company')).data); } catch { /* ignore */ } }
    let logoUrl = (t.logoUrl as string) || '';
    if (!logoUrl) {
      try { logoUrl = (readRow((await apiClient.get('/organization/Company')).data).logoUrl as string) || ''; } catch { /* ignore */ }
    }
    const name = (t.name as string) || '';
    const s = (k: string) => ((t[k] as string) || '') || undefined;
    const signatureUrl = (t.signatureImageUrl as string) || (t.signatureUrl as string) || '';
    const qrUrl = (t.qrImageUrl as string) || (t.qrUrl as string) || '';
    const bankName = (t.bankName as string) || '';
    const bankAccountNo = (t.bankAccountNo as string) || (t.bankAccountNumber as string) || '';
    // Only cache when we actually got something — otherwise leave the cache
    // null so a later print retries instead of being stuck on the fallback.
    if (name || logoUrl) {
      TENANT_CACHE = {
        name: name || undefined,
        logoUrl: logoUrl || undefined,
        address: s('address'),
        city: s('city'),
        state: s('state'),
        pincode: s('pincode'),
        gstin: s('gstin'),
        pan: s('pan'),
        phone: ((t.phone as string) || (t.contactPhone as string)) || undefined,
        email: ((t.email as string) || (t.contactEmail as string)) || undefined,
      };
    }
    // Backend-stored signature / QR / bank — independent of name/logo so a
    // tenant that configured only a signature still primes.
    if (signatureUrl || qrUrl || bankName || bankAccountNo) {
      BRANDING_CACHE = {
        signatureUrl: signatureUrl || undefined,
        qrUrl: qrUrl || undefined,
        bankName: bankName || undefined,
        bankAccountName: (t.bankAccountName as string) || undefined,
        bankAccountNo: bankAccountNo || undefined,
        bankIfsc: (t.bankIfsc as string) || undefined,
        bankBranch: (t.bankBranch as string) || undefined,
      };
    }
  })();
  try { await priming; } finally { priming = null; }
}

/**
 * Tenant/seller header for the print templates. Precedence (highest wins):
 *   manual Settings → Print Formats override (localStorage)
 *   > backend tenant profile (/tenants/Tenant/me, cached)
 *   > 'KSS BillBook' fallback (so prints are never blank).
 * Kicks off the backend prime in the background if it hasn't run yet, so the
 * next print picks up the real name/logo even if the first one didn't.
 */
export function getTenantHeader(): TenantHeader {
  const b = getPrintBranding();
  const t = TENANT_CACHE ?? {};
  if (!TENANT_CACHE && !priming) void primeTenantBranding();
  const gstin = b.tenantGstin || t.gstin || undefined;
  // PAN: explicit override → backend profile → derived from the GSTIN (chars
  // 3–12 are the PAN for a regular Indian GSTIN) so it prints even when the
  // tenant only configured a GSTIN.
  const pan = b.tenantPan || t.pan || (gstin && /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]/i.test(gstin) ? gstin.slice(2, 12).toUpperCase() : undefined);
  return {
    name: b.tenantName || t.name || loginCompanyName() || 'KSS BillBook',
    logoUrl: b.logoUrl || t.logoUrl || undefined,
    address: b.tenantAddress || t.address || undefined,
    city: b.tenantCity || t.city || undefined,
    state: b.tenantState || t.state || undefined,
    pincode: b.tenantPincode || t.pincode || undefined,
    gstin,
    pan,
    phone: b.tenantPhone || t.phone || undefined,
    email: b.tenantEmail || t.email || undefined,
  };
}

/**
 * A QR image for a print. Returns the uploaded/backend QR when present; else
 * auto-generates a verification QR encoding the document's own metadata, so
 * every print shows a QR — dynamic, never hard-coded.
 *
 * The QR is rendered INLINE as an `svg+xml` data: URI (via the bundled `qrcode`
 * encoder). This is deliberate: a print window calls `window.print()` almost
 * immediately, so an EXTERNAL QR image (the old api.qrserver.com URL) usually
 * hadn't finished downloading when the page printed — the QR came out blank.
 * An inline data URI is already in the markup, so it always renders. We keep
 * the external renderer only as a last-resort fallback if encoding throws.
 */
export function printQrUrl(uploaded: string | undefined, parts: Array<string | number | undefined | null>): string {
  if (uploaded) return uploaded;
  const data = parts.map((p) => (p == null ? '' : String(p))).filter(Boolean).join(' | ') || 'KSS BillBook';
  try {
    return qrSvgDataUri(data);
  } catch {
    return `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(data)}`;
  }
}

/**
 * Encode `text` to a QR and return it as a self-contained `data:image/svg+xml`
 * URI. Synchronous (uses `QRCode.create`, not the async `toDataURL`) so it can
 * be dropped straight into a template string.
 */
export function qrSvgDataUri(text: string): string {
  const qr = QRCode.create(text, { errorCorrectionLevel: 'M' });
  const size = qr.modules.size;
  const data = qr.modules.data;
  const quiet = 4; // standard 4-module quiet zone
  const dim = size + quiet * 2;
  let rects = '';
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (data[r * size + c]) rects += `<rect x="${c + quiet}" y="${r + quiet}" width="1" height="1"/>`;
    }
  }
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dim} ${dim}" shape-rendering="crispEdges">` +
    `<rect width="${dim}" height="${dim}" fill="#ffffff"/><g fill="#000000">${rects}</g></svg>`;
  // SVG is pure ASCII here, so btoa is safe.
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

/**
 * Fetch an auth-protected image URL and return it as a self-contained
 * `data:` URI. Uploaded branding (logo/signature/QR) is stored as a hosted
 * `/api/Image/<sha>.png` route that REQUIRES a Bearer header — the in-app
 * `<AuthImg>` adds it, but a print window (`window.open` + `document.write`)
 * renders a plain `<img src>` with NO Authorization header, so the image
 * 401s and prints blank. Inlining the bytes as a data URI makes it render in
 * any print window. Pass-through for `data:`/`blob:` and foreign public URLs.
 * Cached per URL. Falls back to the raw URL on any failure.
 */
const imageInlineCache = new Map<string, string>();
export async function inlineImage(url: string | undefined): Promise<string | undefined> {
  if (!url) return url;
  if (/^(data:|blob:)/i.test(url)) return url;
  // Only auth-fetch OUR image routes; leave foreign public absolute URLs alone.
  const isOwnImage = /\/(api\/)?(Image|uploads)\//i.test(url);
  if (!isOwnImage && /^https?:\/\//i.test(url)) return url;
  if (imageInlineCache.has(url)) return imageInlineCache.get(url);
  try {
    const path = url.replace(/^https?:\/\/[^/]+/i, '').replace(/^\/api\//, '/');
    const r = await apiClient.get(path, { responseType: 'blob' });
    const blob = r.data as Blob;
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result));
      fr.onerror = () => reject(new Error('read failed'));
      fr.readAsDataURL(blob);
    });
    imageInlineCache.set(url, dataUrl);
    return dataUrl;
  } catch {
    return url; // fall back — a public URL may still load
  }
}

/**
 * `getEffectiveBranding()` with logo/signature/QR fetched (with auth) and
 * inlined as data URIs — use this in PRINT paths so uploaded images actually
 * render inside the print window.
 */
export async function getPrintBrandingInlined(): Promise<PrintBranding> {
  const b = getEffectiveBranding();
  const [logoUrl, signatureUrl, qrUrl] = await Promise.all([
    inlineImage(b.logoUrl), inlineImage(b.signatureUrl), inlineImage(b.qrUrl),
  ]);
  return { ...b, logoUrl, signatureUrl, qrUrl };
}

/** `getTenantHeader()` with its logo inlined for print windows. */
export async function getTenantHeaderInlined(): Promise<TenantHeader> {
  const t = getTenantHeader();
  return { ...t, logoUrl: await inlineImage(t.logoUrl) };
}

export function getPrintBranding(): PrintBranding {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as PrintBranding) : {};
  } catch {
    return {};
  }
}

/**
 * Effective branding for prints. Merges, per field, the manual Settings →
 * Print Formats override (localStorage) over the backend tenant profile
 * (signatureImageUrl / qrImageUrl / bank* columns). Use this — not raw
 * getPrintBranding — everywhere a print needs logo/signature/QR/bank, so the
 * values bind dynamically to the backend tenant and never need a local upload
 * once the admin fills them on the Tenant.
 */
export function getEffectiveBranding(): PrintBranding {
  const ls = getPrintBranding();
  const t = TENANT_CACHE ?? {};
  const c = BRANDING_CACHE ?? {};
  if (!TENANT_CACHE && !BRANDING_CACHE && !priming) void primeTenantBranding();
  return {
    ...ls,
    logoUrl: ls.logoUrl || t.logoUrl || undefined,
    signatureUrl: ls.signatureUrl || c.signatureUrl || undefined,
    qrUrl: ls.qrUrl || c.qrUrl || undefined,
    bankName: ls.bankName || c.bankName || undefined,
    bankAccountName: ls.bankAccountName || c.bankAccountName || undefined,
    bankAccountNo: ls.bankAccountNo || c.bankAccountNo || undefined,
    bankIfsc: ls.bankIfsc || c.bankIfsc || undefined,
    bankBranch: ls.bankBranch || c.bankBranch || undefined,
  };
}

export function setPrintBranding(next: PrintBranding): void {
  try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* storage full / disabled */ }
}

export function resetPrintBranding(): void {
  try { localStorage.removeItem(LS_KEY); } catch { /* */ }
}
